import { UPDATE_NOTIFICATIONS } from '@common/constants/webSockets'
import {
  INotification,
  INotificationCounts,
  INotificationData,
  NotificationHome,
} from '@common/interfaces/notification'
import { combineReducers } from 'redux'
import { loadingArrayReducer } from '../../helpers/helpers'
import { IAction, IActionWithParams, IPaginationParams } from '../apiMiddleware'
import * as actions from './actions'

interface IMarkAsReadParams {
  notificationIds?: string[]
  all?: boolean
  home?: NotificationHome
}

interface IListParams extends IPaginationParams {
  home?: NotificationHome
  filters?: Record<string, unknown>
}

/** One tab of the Notifications list. */
export interface IHomeNotifications {
  // The tab's loaded rows; null until its first response lands. That is what lets the page tell
  // "still loading" apart from "loaded and empty" instead of flashing an empty state it hasn't
  // earned — a REQUEST records its key here before any rows exist.
  data: INotification[] | null
  total: number
  // Identifies the filters the most recent request for this tab went out with. `apiMiddleware`
  // has no cancellation, so a response for filters the user has already moved off can land last
  // and clobber the current ones; a response whose key no longer matches is dropped instead.
  requestKey: string | null
}

/**
 * The Notifications page holds all four tabs at once, so switching between them renders from
 * what is already loaded rather than waiting on a fetch. Rows are per-tab; the unread counts are
 * not — the server returns the same INotificationCounts whatever tab is asked for, so they live
 * once here instead of four times over, and a mark-read adjusts them in one place.
 */
export interface INotificationsState {
  byHome: Partial<Record<NotificationHome, IHomeNotifications>>
  counts: INotificationCounts
}

const INITIAL_COUNTS: INotificationCounts = {
  totalUnread: 0,
  actionItemsUnread: 0,
  inboxUnread: 0,
}

const INITIAL_NOTIFICATIONS_STATE: INotificationsState = {
  byHome: {},
  counts: INITIAL_COUNTS,
}

const pickCounts = ({
  totalUnread,
  actionItemsUnread,
  inboxUnread,
}: INotificationCounts): INotificationCounts => ({ totalUnread, actionItemsUnread, inboxUnread })

const buildRequestKey = (params: IListParams | undefined): string =>
  JSON.stringify(params?.filters ?? null)

interface ISettledHome {
  home: NotificationHome
  entry: IHomeNotifications | undefined
  requestKey: string
}

// The tab a list response settles, or null when it should be dropped: it names no tab, or it is
// for filters the tab has since moved off (see IHomeNotifications.requestKey).
const settledHome = (
  state: INotificationsState,
  params: IListParams | undefined,
): ISettledHome | null => {
  const home = params?.home
  if (!home) {
    return null
  }
  const entry = state.byHome[home]
  const requestKey = buildRequestKey(params)
  if (entry?.requestKey && entry.requestKey !== requestKey) {
    return null
  }
  return { home, entry, requestKey }
}

// Merge a websocket refresh payload (`UPDATE_NOTIFICATIONS`, which spans both ACTION_ITEMS and
// INBOX) into a currently-loaded notification list. Only flips a loaded row to read when its
// matching row in the payload is ITSELF read — a row can appear in the payload while still
// unread, so matching on id presence alone (ignoring the matched row's `isRead`) can wrongly mark
// an unread row from an untouched tab as read if a scoped mark-as-read for a different tab
// happens to race this refresh in.
const mergeUpdatedNotifications = (
  data: INotification[],
  updatedData: INotification[],
): INotification[] =>
  data.map((notification) => {
    const updated = updatedData.find(({ id }) => id === notification.id)
    return updated?.isRead && !notification.isRead
      ? { ...notification, isRead: true }
      : notification
  })

const mapLoadedHomes = (
  byHome: INotificationsState['byHome'],
  update: (data: INotification[]) => INotification[],
): INotificationsState['byHome'] =>
  Object.fromEntries(
    Object.entries(byHome).map(([home, entry]) => [
      home,
      entry.data ? { ...entry, data: update(entry.data) } : entry,
    ]),
  )

type IClearedByHome = Partial<Record<NotificationHome, number>>

// Flip every loaded row a "mark read" covers, recording each cleared row's home by id so the
// counts can be decremented by the same amount. Keyed by id because the bell and the page can
// both hold the same row. `home` scopes the flip to one tab.
const markRowsRead = (
  data: INotification[],
  params: IMarkAsReadParams | undefined,
  clearedHomes: Map<string, NotificationHome>,
): INotification[] => {
  const ids = new Set(params?.notificationIds ?? [])
  return data.map((notification) => {
    const matchesHome = !params?.home || notification.home === params.home
    if (!notification.isRead && matchesHome && (params?.all || ids.has(notification.id))) {
      if (notification.home) {
        clearedHomes.set(notification.id, notification.home)
      }
      return { ...notification, isRead: true }
    }
    return notification
  })
}

const tallyByHome = (clearedHomes: Map<string, NotificationHome>): IClearedByHome => {
  const clearedByHome: IClearedByHome = {}
  clearedHomes.forEach((home) => {
    clearedByHome[home] = (clearedByHome[home] ?? 0) + 1
  })
  return clearedByHome
}

// Counts after an optimistic "mark read".
//
// `all` clears the whole home server-side: MarkAsRead updates every target row for that home,
// ignoring both `notificationIds` and the list's active filters. So a scoped clear zeroes that
// home's count outright — decrementing by the rows that happen to be loaded would strand the
// badge above zero on any paginated or filtered list until the websocket refresh lands.
//
// A per-id "Mark read" carries no `home` at all, so it decrements the cleared row's OWN `home`
// rather than `params.home` — the bell-glance list it can fire from spans both tabs.
const countsAfterMarkRead = (
  counts: INotificationCounts,
  params: IMarkAsReadParams | undefined,
  clearedByHome: IClearedByHome,
): INotificationCounts => {
  const nextCount = (home: NotificationHome, count: number) =>
    params?.all && (!params.home || params.home === home)
      ? 0
      : Math.max(0, count - (clearedByHome[home] ?? 0))
  const actionItemsUnread = nextCount(NotificationHome.ACTION_ITEMS, counts.actionItemsUnread)
  const inboxUnread = nextCount(NotificationHome.INBOX, counts.inboxUnread)
  return {
    // The bell counts Action Items only, so it always matches that tab's number. A Messages row
    // was never in the bell, so clearing that tab moves none of them.
    totalUnread: actionItemsUnread,
    actionItemsUnread,
    inboxUnread,
  }
}

const sliceReducers = combineReducers({
  loadingArray: loadingArrayReducer([actions.prefix]),
  lastNotifications: (state: INotificationData = null, action: IAction): INotificationData => {
    switch (action.type) {
      case actions.LIST_LAST_NOTIFICATIONS_SUCCESS:
      case UPDATE_NOTIFICATIONS:
        return action.data as INotificationData
      default:
        return state
    }
  },
  notifications: (
    state: INotificationsState = INITIAL_NOTIFICATIONS_STATE,
    action: IActionWithParams<IListParams & IMarkAsReadParams>,
  ): INotificationsState => {
    switch (action.type) {
      case actions.LIST_NOTIFICATIONS_REQUEST: {
        const home = action.params?.home
        if (!home) {
          return state
        }
        const entry = state.byHome[home]
        return {
          ...state,
          byHome: {
            ...state.byHome,
            [home]: { data: null, total: 0, ...entry, requestKey: buildRequestKey(action.params) },
          },
        }
      }
      case actions.LIST_NOTIFICATIONS_SUCCESS: {
        const settled = settledHome(state, action.params)
        if (!settled) {
          return state
        }
        const { home, entry, requestKey } = settled
        const { data, total, ...counts } = action.data as INotificationData
        return {
          // Counts are whole-account scalars, so any tab's response refreshes all of them.
          counts: pickCounts(counts),
          byHome: {
            ...state.byHome,
            [home]: {
              requestKey,
              total,
              // `loadMore` appends a page; anything else replaces what the tab was showing.
              data: action.params?.loadMore && entry?.data ? [...entry.data, ...data] : data,
            },
          },
        }
      }
      case actions.LIST_NOTIFICATIONS_FAILURE: {
        const settled = settledHome(state, action.params)
        if (!settled) {
          return state
        }
        const { home, entry, requestKey } = settled
        // The tab must stop reading as "still loading": with no rows it falls through to the empty
        // state, and a refresh that failed keeps the rows it was refreshing on screen.
        return {
          ...state,
          byHome: {
            ...state.byHome,
            [home]: { requestKey, total: entry?.total ?? 0, data: entry?.data ?? [] },
          },
        }
      }
      case UPDATE_NOTIFICATIONS: {
        // The websocket payload is a full INotificationData object (last-N items + counts,
        // spanning both ACTION_ITEMS and INBOX) — merge in any of our loaded items it confirms
        // as read (see mergeUpdatedNotifications for why matching on id alone isn't enough), and
        // refresh the counts.
        const updatedData = action.data as INotificationData
        return {
          counts: pickCounts(updatedData),
          byHome: mapLoadedHomes(state.byHome, (data) =>
            mergeUpdatedNotifications(data, updatedData.data),
          ),
        }
      }
      default:
        return state
    }
  },
})

type INotificationRootState = ReturnType<typeof sliceReducers>

// Optimistic "mark read" across the bell and the page at once. Both slices decrement by one tally
// of the rows either has loaded, so the bell drops with the Action Items tab even when the cleared
// row is only loaded on one side — the bell holds just the latest few.
const applyOptimisticMarkRead = (
  state: INotificationRootState,
  params: IMarkAsReadParams | undefined,
): INotificationRootState => {
  const clearedHomes = new Map<string, NotificationHome>()
  const flipRead = (data: INotification[]) => markRowsRead(data, params, clearedHomes)
  const byHome = mapLoadedHomes(state.notifications.byHome, flipRead)
  const bellData = state.lastNotifications && flipRead(state.lastNotifications.data)
  const clearedByHome = tallyByHome(clearedHomes)
  return {
    ...state,
    lastNotifications: state.lastNotifications && {
      ...state.lastNotifications,
      data: bellData,
      ...countsAfterMarkRead(state.lastNotifications, params, clearedByHome),
    },
    notifications: {
      byHome,
      counts: countsAfterMarkRead(state.notifications.counts, params, clearedByHome),
    },
  }
}

export default (
  state: INotificationRootState | undefined,
  action: IActionWithParams<IListParams & IMarkAsReadParams>,
): INotificationRootState => {
  const nextState = sliceReducers(state, action)
  return action.type === actions.MARK_NOTIFICATIONS_AS_READ_REQUEST
    ? applyOptimisticMarkRead(nextState, action.params)
    : nextState
}
